import React, { useEffect, useState } from 'react';
import styles from './DeleteAssets.module.scss';
import MainButton from 'components/StyledComponents/MainButton';
import ConfirmationPopup from 'components/ConfirmationPopup/ConfirmationPopup';
import { DELETE_ASSET } from 'constants/dialogPopupsData';
import { getErrorsProperties } from 'helpers/ErrorValidator';
import { tabs, defaultTab } from '../helpers';
import { getOtherTabNames, getUpdatedTabValues, getUniqueAssets } from '../helpers';
import { useBatchAssetUpdatesActions, useBatchAssetUpdatesSelector } from 'hooks/BatchAssetUpdates';
import { useFormContext } from 'react-hook-form';
import { useUserConfig } from 'hooks/useUserConfig';
import {
  FormLabel,
  FormMultipleAssetSelect,
  FormTextArea,
  FormSelectInput
} from 'components/FormComponents';
import FormAutoMultiSelect from 'components/FormComponents/FormAutoMultiSelect/FormAutoMultiSelect';
import { BlueButton } from 'components/Buttons';

export default function DeleteAssets() {
  const tabName = tabs.deleteAssets;

  const { isConfigReceived, isAdminUser, isOperationsManagerUser } = useUserConfig();

  const { setError, getValues, setValue, clearErrors, formState, watch } = useFormContext();
  const getFieldName = (name) => `${tabName}.${name}`;

  const getFormValues = (name) => getValues(getFieldName(name));
  const setFormValue = (name, value) => setValue(getFieldName(name), value);

  const { errors } = formState;
  const isFormInvalid = () => !!errors?.[tabName]?.['assets'];

  const { creationForm } = useBatchAssetUpdatesSelector();
  const { setTabAction, getAssetsAction, deleteAssetsAction } = useBatchAssetUpdatesActions();

  const { locations, assetPrefixes } = creationForm;

  const [modalData, setModalData] = useState({});

  useEffect(() => {
    if (!isConfigReceived) return;
    if (isAdminUser || isOperationsManagerUser) return;

    setTabAction(defaultTab);
  }, [isConfigReceived]);

  useEffect(() => {
    return () => {
      clearErrors(tabName);
    };
  }, []);

  const validateForm = () => {
    const assets = getFormValues('assets');
    const deleteReason = getFormValues('deleteReason');
    const notes = getFormValues('notes');

    if (!assets?.length) {
      setError(getFieldName('assets'), getErrorsProperties('Assets field is required'));
      return false;
    }

    if (!deleteReason) {
      setError(getFieldName('deleteReason'), getErrorsProperties('Reason is required'));
      return false;
    }

    if (deleteReason?.id === 4 && !notes?.trim()) {
      setError(
        getFieldName('notes'),
        getErrorsProperties('Notes are required when selecting "Other"')
      );
      return false;
    }

    return true;
  };

  const handleUpdateClick = () => {
    if (!validateForm()) return;
    setModalData({ ...DELETE_ASSET, isOpened: true });
  };

  const handleAssetSelect = (codes) => {
    getOtherTabNames(tabName).forEach((name) =>
      setValue(name, getUpdatedTabValues(name, codes, getValues()))
    );
    if (errors?.[tabName]?.['assets']?.message) {
      clearErrors(getFieldName('assets'));
    }
    setFormValue('assets', codes);
  };

  const clearForm = () => {
    setFormValue('assets', []);
    setFormValue('filterLocationIds', []);
    setFormValue('filterAssetPrefixesIds', []);
    setFormValue('notes', '');
    setFormValue('deleteReason', null);
    getOtherTabNames(tabName).forEach((name) =>
      setValue(name, getUpdatedTabValues(name, [], getValues()))
    );
  };

  const closeModal = () => setModalData({});
  const agreeModal = () => {
    closeModal();

    const values = getValues(tabName);
    const assetIds = values.assets.map((a) => (typeof a === 'object' ? a.id : a));
    const statusId = values.deleteReason?.id;
    const note = values.notes;

    const query = [
      ...assetIds.map((id) => `assetIds=${encodeURIComponent(id)}`),
      `statusId=${encodeURIComponent(statusId)}`
    ];

    if (note?.trim()) {
      query.push(`deletionNote=${encodeURIComponent(note.trim())}`);
    }

    const fullQuery = query.join('&');

    deleteAssetsAction(fullQuery).then(clearForm);
  };

  const locationIdsWatcher = watch(getFieldName('filterLocationIds'));
  const assetPrefixesIdsWatcher = watch(getFieldName('filterAssetPrefixesIds'));

  const addAssetsByParameters = async () => {
    const query = {
      filters: {
        isDeleted: false,
        locationIds: locationIdsWatcher,
        prefixIds: assetPrefixesIdsWatcher
      }
    };
    const { items = [] } = await getAssetsAction(query);

    if (!items?.length) return;

    const codes = getUniqueAssets([...items, ...getFormValues('assets')]);
    setFormValue('assets', codes);
    handleAssetSelect(codes);
  };

  return (
    <section className={styles.wrapper}>
      <ConfirmationPopup data={modalData} onAgree={agreeModal} onDismiss={closeModal} />
      <div className={styles.form}>
        <div className={styles.form__row}>
          <FormLabel>Narrow Assets by Prefix</FormLabel>
          <FormAutoMultiSelect
            name={getFieldName('filterAssetPrefixesIds')}
            menuItems={assetPrefixes || []}
            options={{
              label: 'prefix',
              disableByObjectTracker: true,
              disableLabel: true
            }}
          />
        </div>
        <div className={styles.form__row}>
          <FormLabel>Narrow Assets by Location</FormLabel>
          <FormAutoMultiSelect
            name={getFieldName('filterLocationIds')}
            menuItems={locations || []}
            options={{
              label: 'siteCode',
              disableByObjectTracker: true,
              disableLabel: true
            }}
          />
        </div>

        {(!!locationIdsWatcher?.length || !!assetPrefixesIdsWatcher?.length) && (
          <div className={styles.form__add_assets}>
            <BlueButton
              onClick={addAssetsByParameters}
              label="Add all assets"
              className={styles.form__add_assets_button}
            />
            <FormLabel>or enter Asset DR Codes manually</FormLabel>
          </div>
        )}

        <div className={styles.form__assets}>
          <FormLabel required>Enter Asset DR Codes; codes must be valid DR Codes</FormLabel>
          <FormMultipleAssetSelect
            withQr
            name={getFieldName('assets')}
            onSelectTriggered={handleAssetSelect}
            queryOptions={{ locationIds: locationIdsWatcher, prefixIds: assetPrefixesIdsWatcher }}
          />
        </div>

        <div className={styles.form__row}>
          <FormLabel required>Reason for Deleting</FormLabel>
          <FormSelectInput
            name={getFieldName('deleteReason')}
            options={[
              { name: 'Donated', id: 1 },
              { name: 'Disposed', id: 2 },
              { name: 'Duplicate', id: 3 },
              { name: 'Other', id: 4 }
            ]}
            onSelect={({ value }) => {
              clearErrors(getFieldName('deleteReason'));
              setValue(getFieldName('deleteReason'), value ?? null);
              console.log('onSelect value', value);
            }}
            enrichNameToId
          />
        </div>

        <div className={styles.form__notes}>
          <FormLabel>Deletion Notes</FormLabel>
          <FormTextArea name={getFieldName('notes')} options={{ max: 255 }} />
        </div>
      </div>
      <div className={styles.footer}>
        <MainButton
          text="delete"
          type="primary"
          isDisabled={isFormInvalid()}
          action={handleUpdateClick}
        />
      </div>
    </section>
  );
}
